import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { worklogService } from "@/services/worklogService";
import { isEmployee } from "@/constants/userTypes";
import { cn } from "@/lib/utils";

const fmt = (iso: string) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * 일일 업무 보고 미등록 배너 (비차단 알림).
 * - 마감 경과 미등록(missing): 빨강
 * - 마감 전 미등록(전 근무일, pending): 노랑 — "오늘 HH:MM까지 등록"
 * 업무일지 화면에서는 표시하지 않는다.
 */
export default function DailyReportBanner() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);

  const { data: status } = useQuery({
    queryKey: ["daily-report-status"],
    queryFn: () => worklogService.getRequiredStatus(),
    enabled: isAuthenticated && !!user && isEmployee(user.user_type),
    staleTime: 5 * 60 * 1000,
  });

  if (dismissed || !status?.enabled) return null;
  if (location.pathname.startsWith("/worklog")) return null;

  const missing = status.missing_dates;
  const pending = status.pending_dates;
  if (missing.length === 0 && pending.length === 0) return null;

  const isMissing = missing.length > 0;
  const target = isMissing ? missing[0] : pending[0].date;
  const message = isMissing
    ? `마감이 지난 일일 업무 보고 ${missing.length}건이 미등록 상태입니다. (${missing.map(fmt).join(", ")})`
    : `${fmt(pending[0].date)} 일일 업무 보고를 ${
        pending[0].deadline_at ? `${fmt(pending[0].deadline_at)} ${new Date(pending[0].deadline_at).toTimeString().slice(0, 5)}` : "마감"
      }까지 등록해주세요.`;

  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b px-4 py-2 text-sm",
        isMissing
          ? "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200"
          : "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/40 dark:text-amber-100",
      )}
    >
      {isMissing ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <BookOpen className="h-4 w-4 shrink-0" />}
      <span className="flex-1 truncate">{message}</span>
      <Button asChild size="sm" variant={isMissing ? "destructive" : "default"} className="h-7">
        <Link to={`/worklog?date=${target}`}>등록하기</Link>
      </Button>
      <button type="button" onClick={() => setDismissed(true)} className="opacity-70 hover:opacity-100" aria-label="닫기">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
